/** MI DOMINO - the player's session on this device: the sidebar account menu
 *  (edit profile · change password · log out · how to play), logging out, the
 *  first-launch door vs. the returning player's daily gift, and the
 *  session-expired / guest-merged notices. */
import { renderEnroll, setToken } from '../admin/portal';
import { ONLINE_MODE } from '../data/config';
import { checkDailyReward } from '../economy/daily';
import { applyLang } from '../game/engine';
import { api } from '../net/api';
import { ACCOUNT, API_TOKEN, setDashView } from '../state';
import { openDash } from './dashboard';
import { $ } from './dom';
import { LANG } from './i18n';
import { showHowTo } from './onboarding';
import { toast } from './toast';

/* ---------- ACCOUNT MENU (sidebar): edit profile · change password · log out ---------- */
export function doLogout(everywhere = false): void {
  const q = everywhere
    ? (LANG === 'en' ? 'Log out on EVERY device? All your sessions will end.' : '¿Cerrar sesión en TODOS los dispositivos? Terminarán todas tus sesiones.')
    : (LANG === 'en' ? 'Log out of this device? Your account stays safe on the server.' : '¿Cerrar sesión en este dispositivo? Tu cuenta queda segura en el servidor.');
  if (!confirm(q)) return;
  // tell the server so the token stops working (best effort — offline logout still logs out locally)
  const done = () => {
    try { setToken(''); } catch (e) { /* */ }
    try {
      for (const k of ['account', 'wallet', 'stats', 'profile', 'team', 'admindb']) localStorage.removeItem(k);
      sessionStorage.removeItem('guest');
    } catch (e) { /* */ }
    location.reload();
  };
  // ({} body → POST; without a body api() would GET and the revoke would never fire)
  // never let a slow or dead network keep you signed in: wait at most 3 s for the server, then log out anyway
  if (ONLINE_MODE && API_TOKEN) Promise.race([
    api(everywhere ? '/api/logout-all' : '/api/logout', {}),
    new Promise(r => setTimeout(r, 3000)),
  ]).then(done, done);
  else done();
}

/** Change password while logged in: current + new → /api/password (rotates the token). */
export function showPasswordModal(): void {
  const en = LANG === 'en';
  const ov = document.createElement('div');
  ov.id = 'pw-ov';
  ov.style.cssText = 'position:fixed;inset:0;z-index:9700;background:rgba(6,10,20,.88);display:flex;align-items:center;justify-content:center;padding:16px';
  ov.innerHTML = `<div class="modal" style="max-width:360px;width:100%">
    <h2 style="font-size:20px">🔐 ${en ? 'Change password' : 'Cambiar contraseña'}</h2>
    <input id="pw-cur" type="password" placeholder="${en ? 'Current password' : 'Contraseña actual'}" autocomplete="current-password">
    <input id="pw-new" type="password" placeholder="${en ? 'New password (6+ chars)' : 'Nueva contraseña (6+ caracteres)'}" autocomplete="new-password">
    <p id="pw-err" style="color:#e05555;font-size:12px;min-height:15px;margin:4px 0 0"></p>
    <button class="btn gold" id="pw-go">${en ? 'SAVE' : 'GUARDAR'}</button>
    <button class="btn" id="pw-cancel" style="background:#3a4a75;margin-top:8px">${en ? 'Cancel' : 'Cancelar'}</button>
  </div>`;
  document.body.appendChild(ov);
  const q = (sel: string): any => ov.querySelector(sel);
  q('#pw-cancel').onclick = () => ov.remove();
  q('#pw-go').onclick = async () => {
    const cur = q('#pw-cur').value, nw = q('#pw-new').value, err = q('#pw-err');
    if (nw.length < 6) { err.textContent = en ? 'New password: 6+ characters.' : 'Nueva contraseña: 6+ caracteres.'; return; }
    try {
      const res = await api('/api/password', { current: cur, pw: nw });
      if (res && res.token) setToken(res.token);   // server rotated the session token
      ov.remove();
      toast(en ? '🔐 Password updated' : '🔐 Contraseña actualizada');
    } catch (ex: any) { err.textContent = ex.message; }
  };
}

/** Wire the top-bar account chip + dropdown (needs #btn-account / #acct-menu). */
export function initAccountMenu(): void {
  const btn = $('btn-account'), menu = $('acct-menu');
  if (!btn || !menu) return;
  const loggedIn = !!ACCOUNT.email;
  // Guests (no account on this device) are NOT logged in: no profile/password/log-out
  // items — they get a single "Sign in / Create account" entry instead. The
  // real account items only exist for a signed-in player.
  const pw = $('acct-password');
  // visibility of profile/password/logout vs. Sign-in (and the Dashboard tab)
  // is decided in applyLang() from ACCOUNT.email — LIVE, so a signup without a
  // page reload flips the menu too. Here we only wire the handlers.
  const li = $('acct-login');
  if (li) li.onclick = () => { menu.style.display = 'none'; renderEnroll('login'); };
  if (!loggedIn) document.body.classList.add('guest');
  applyLang();   // account chip label + menu items follow the login state
  btn.onclick = () => { menu.style.display = menu.style.display === 'flex' ? 'none' : 'flex'; };
  $('acct-profile').onclick = () => { menu.style.display = 'none'; setDashView('editar'); openDash(); };
  if (pw) pw.onclick = () => { menu.style.display = 'none'; showPasswordModal(); };
  $('acct-logout').onclick = () => { menu.style.display = 'none'; doLogout(); };
  const la = $('acct-logout-all');
  if (la) la.onclick = () => { menu.style.display = 'none'; doLogout(true); };
  // ❓ How to play — always reachable (the onboarding tips are one-step now)
  const hb = $('btn-help');
  if (hb) hb.onclick = () => { menu.style.display = 'none'; showHowTo(() => {}, true); };
  // phones hide New match / Language from the bar → proxy them from the menu
  // (single source of truth: the real buttons' handlers run)
  const mn = $('menu-newmatch');
  if (mn) mn.onclick = () => { menu.style.display = 'none'; $('new-match')?.click(); };
  const ml = $('menu-lang');
  if (ml) ml.onclick = () => { menu.style.display = 'none'; $('btn-lang')?.click(); };
  // click outside closes the dropdown
  document.addEventListener('click', (ev: any) => {
    if (menu.style.display === 'flex' && !$('acct-wrap')?.contains(ev.target)) menu.style.display = 'none';
  });
}

/** First visit → the door; returning players get their daily gift. Also the
 *  one-shot notices set by the previous page load (session revoked / guest
 *  progress merged). Runs last in the bootstrap: the door goes over a fully
 *  wired app. */
export function initSession(): void {
  try {
    // The door (create account / sign in) is the FIRST screen on every launch
    // without an account. Playing as a guest is a deliberate choice made inside
    // it ("Just looking — try as guest"), never the default.
    if (!ACCOUNT.email) renderEnroll('signup');
    else checkDailyReward();
    // this device's session was revoked (logged out elsewhere / password changed / banned)
    const exp = sessionStorage.getItem('mdexpired');
    if (exp) {
      sessionStorage.removeItem('mdexpired');
      const banned = /suspendida/.test(exp);
      setTimeout(() => toast(banned
        ? (LANG === 'en' ? '🚫 This account is suspended. Contact support.' : '🚫 Esta cuenta está suspendida. Contacta soporte.')
        : (LANG === 'en' ? '🔒 Your session ended — sign in again' : '🔒 Tu sesión terminó — vuelve a entrar')), 900);
    }
    // guest progress just merged into this account (set right before the post-login reload)
    const merged = sessionStorage.getItem('mdmerged');
    if (merged) {
      sessionStorage.removeItem('mdmerged');
      setTimeout(() => toast(merged === 'charged'
        ? (LANG === 'en' ? '🎒 Your guest purchases joined this account (paid with your coins)' : '🎒 Tus compras de invitado se unieron a esta cuenta (pagadas con tus monedas)')
        : (LANG === 'en' ? '🎒 Your guest coins, purchases & stats joined this account' : '🎒 Tus monedas, compras y stats de invitado se unieron a esta cuenta')), 1600);
    }
    const dropped = sessionStorage.getItem('mddropped');
    if (dropped) {
      sessionStorage.removeItem('mddropped');
      setTimeout(() => toast(LANG === 'en' ? `⚠️ ${dropped} guest purchase(s) could not be kept — not enough coins on this account` : `⚠️ ${dropped} compra(s) de invitado no se pudieron conservar — faltan monedas en esta cuenta`), 5200);
    }
  } catch (e) { /* */ }
}
